import { addDays, isIsoDate } from "./dates";
import { monthsBack } from "./dashboard";
import { DomainError } from "./errors";
import { daysBetween } from "./finance";

/**
 * BI histórico (T3.9, RF-BI-10..12). Os fatos de venda saem do TenantDO para o lago (R2 Data Catalog/Iceberg) como um
 * **razão de lançamentos**: cada pedido exportado gera linhas `sign = +1` com a fotografia atual e, se já tinha sido
 * enviado e mudou, `sign = −1` com a fotografia anterior. Somar `sign × valor` dá o estado atual sem UPDATE/DELETE no
 * lago (o Pipelines só acrescenta). Um lote reenviado tem o mesmo `export_id`: a leitura deduplica por
 * `(export_id, order_id, line, sign)`. Tudo inteiro (centavos, q4) e somado em BigInt; percentuais em pct4
 * (100% = 1.000.000).
 */

/** Linha da fotografia de um pedido: `line = ''` é o pedido; `line = <product_id>` é um produto do pedido. */
export type FactLine = {
  day: string;
  orderId: string;
  line: string;
  contactId: string;
  statusId: string;
  sellerKey: string;
  storeKey: string;
  uf: string;
  orders: number;
  qtyQ4: number;
  revenueCents: number;
  freightCents: number;
  costCents: number;
  costMissing: number;
};
export type FactDelta = FactLine & { sign: 1 | -1 };

/** Registro do lago (`cx_erp.sales_facts`), em snake_case: é o schema do stream do Pipelines e da tabela Iceberg. */
export type FactRecord = {
  tenant_id: string;
  company_id: string;
  month: string;
  day: string;
  order_id: string;
  line: string;
  sign: 1 | -1;
  contact_id: string;
  status_id: string;
  seller_key: string;
  store_key: string;
  uf: string;
  orders: number;
  qty_q4: number;
  revenue_cents: number;
  freight_cents: number;
  cost_cents: number;
  cost_missing: number;
  export_id: string;
  exported_at: string;
};

export const ANALYTICS_DEFAULT_TABLE = "cx_erp.sales_facts";
/** Linhas agregadas por consulta (cliente × dia, produtos): acima, `analytics_too_large`. */
export const ANALYTICS_MAX_ROWS = 200_000;
/** Linhas por lote do export (o lote vai inteiro numa chamada ao Pipelines / num arquivo). */
export const ANALYTICS_EXPORT_BATCH_ROWS = 2_000;
/** Período máximo das telas do histórico. */
export const ANALYTICS_MAX_MONTHS = 36;

const sameLine = (a: FactLine, b: FactLine) =>
  a.day === b.day &&
  a.orderId === b.orderId &&
  a.line === b.line &&
  a.contactId === b.contactId &&
  a.statusId === b.statusId &&
  a.sellerKey === b.sellerKey &&
  a.storeKey === b.storeKey &&
  a.uf === b.uf &&
  a.orders === b.orders &&
  a.qtyQ4 === b.qtyQ4 &&
  a.revenueCents === b.revenueCents &&
  a.freightCents === b.freightCents &&
  a.costCents === b.costCents &&
  a.costMissing === b.costMissing;

/**
 * Lançamentos entre a fotografia enviada (`before`) e a atual (`after`), por `line`: igual = nada; mudou = −antiga e
 * +nova; só antes = −; só agora = +. Ordem: pela ordem de `before` e depois as novas de `after`.
 */
export function factDeltas(before: readonly FactLine[], after: readonly FactLine[]): FactDelta[] {
  const now = new Map(after.map((l) => [l.line, l]));
  const was = new Map(before.map((l) => [l.line, l]));
  const out: FactDelta[] = [];
  for (const b of before) {
    const a = now.get(b.line);
    if (a && sameLine(a, b)) continue;
    out.push({ ...b, sign: -1 });
    if (a) out.push({ ...a, sign: 1 });
  }
  for (const a of after) if (!was.has(a.line)) out.push({ ...a, sign: 1 });
  return out;
}

export type ExportMeta = { tenantId: string; companyId: string; exportId: string; exportedAt: string };

export function toFactRecords(deltas: readonly FactDelta[], m: ExportMeta): FactRecord[] {
  return deltas.map((d) => ({
    tenant_id: m.tenantId,
    company_id: m.companyId,
    month: d.day.slice(0, 7),
    day: d.day,
    order_id: d.orderId,
    line: d.line,
    sign: d.sign,
    contact_id: d.contactId,
    status_id: d.statusId,
    seller_key: d.sellerKey,
    store_key: d.storeKey,
    uf: d.uf,
    orders: d.orders,
    qty_q4: d.qtyQ4,
    revenue_cents: d.revenueCents,
    freight_cents: d.freightCents,
    cost_cents: d.costCents,
    cost_missing: d.costMissing,
    export_id: m.exportId,
    exported_at: m.exportedAt,
  }));
}

// ---------- literais seguros (partição local e SQL do R2 SQL) ----------

const SAFE_ID = /^[0-9A-Za-z_-]{1,64}$/;
const MONTH = /^\d{4}-(0[1-9]|1[0-2])$/;
const TABLE = /^[a-z_][a-z0-9_]{0,62}\.[a-z_][a-z0-9_]{0,62}$/;

const bad = (what: string): never => {
  throw new DomainError("analytics_invalid_literal", `valor inválido para a consulta: ${what}`);
};
const safeId = (v: string, what: string) => (SAFE_ID.test(v) ? v : bad(what));
/** Chave de loja/vendedor: vazia (venda direta / sem vendedor) ou identificador. */
const safeKey = (v: string, what: string) => (v === "" ? v : safeId(v, what));
const safeDate = (v: string) => (isIsoDate(v) ? v : bad("data"));
const safeMonth = (v: string) => (MONTH.test(v) ? v : bad("mês"));

/** Prefixo dos arquivos do armazenamento local: `analytics/v1/t/{tenant}/c/{empresa}/[m={aaaa-mm}/]`. */
export function analyticsPartitionPrefix(tenantId: string, companyId: string, month?: string): string {
  const base = `analytics/v1/t/${safeId(tenantId, "tenant")}/c/${safeId(companyId, "empresa")}/`;
  return month === undefined ? base : `${base}m=${safeMonth(month)}/`;
}

// ---------- período ----------

/** Meses `aaaa-mm` de `from` a `to` (datas), inclusive. */
export function analyticsMonths(from: string, to: string): string[] {
  const out: string[] = [];
  for (let m = from.slice(0, 7); m <= to.slice(0, 7); m = addDays(`${m}-28`, 7).slice(0, 7)) out.push(m);
  return out;
}

/** Padrão: dia 1º de 11 meses atrás até hoje (12 meses com o corrente). */
export function defaultAnalyticsPeriod(today: string): { from: string; to: string } {
  return { from: `${monthsBack(today.slice(0, 7), 12)[0]!}-01`, to: today };
}

export function analyticsPeriodProblem(from: string, to: string): string | null {
  if (from > to) return "A data de início deve ser anterior ou igual à data final";
  if (analyticsMonths(from, to).length > ANALYTICS_MAX_MONTHS)
    return `O período pode ter no máximo ${ANALYTICS_MAX_MONTHS} meses`;
  return null;
}

// ---------- agregação (armazenamento local; a mesma conta do SQL do R2 SQL) ----------

export type AnalyticsScope = { tenantId: string; companyId: string };
/**
 * Recorte de toda consulta: faixa de dias, situações que contam como venda, canais (lojas; `''` = venda direta) e o
 * vendedor da carteira (usuário-vendedor). Tenant e empresa vêm do `AnalyticsScope`, nunca do filtro.
 */
export type FactFilter = {
  from: string;
  to: string;
  statusIds: readonly string[];
  storeKeys?: readonly string[] | null;
  sellerKey?: string | null;
};

export type ProductTotal = {
  productId: string;
  orders: bigint;
  qtyQ4: bigint;
  revenueCents: bigint;
  costCents: bigint;
  costMissing: bigint;
};
export type CustomerDay = { contactId: string; day: string; orders: bigint; revenueCents: bigint };
export type CustomerMonth = { contactId: string; month: string; orders: bigint; revenueCents: bigint };
export type CustomerActivity = {
  contactId: string;
  firstDay: string;
  lastDay: string;
  orders: bigint;
  revenueCents: bigint;
};

/** Registros do recorte, sem repetição do mesmo lançamento (lote reenviado). */
function scoped(
  records: readonly FactRecord[],
  s: AnalyticsScope,
  f: FactFilter,
  kind: "order" | "product",
): FactRecord[] {
  const statuses = new Set(f.statusIds);
  const stores = f.storeKeys ? new Set(f.storeKeys) : null;
  const seen = new Set<string>();
  const out: FactRecord[] = [];
  for (const r of records) {
    if (r.tenant_id !== s.tenantId || r.company_id !== s.companyId) continue;
    if ((kind === "order") !== (r.line === "")) continue;
    if (r.day < f.from || r.day > f.to || !statuses.has(r.status_id)) continue;
    if (stores && !stores.has(r.store_key)) continue;
    if (f.sellerKey != null && r.seller_key !== f.sellerKey) continue;
    const k = `${r.export_id}|${r.order_id}|${r.line}|${r.sign}`;
    if (seen.has(k)) continue;
    seen.add(k);
    out.push(r);
  }
  return out;
}

const tooLarge = (n: number) => {
  if (n > ANALYTICS_MAX_ROWS)
    throw new DomainError(
      "analytics_too_large",
      "O recorte tem dados demais para esta tela. Escolha um período menor ou filtre por canal.",
    );
};

/** Produtos no recorte (pedidos líquidos > 0), por receita decrescente. */
export function aggregateProducts(
  records: readonly FactRecord[],
  s: AnalyticsScope,
  f: FactFilter,
): ProductTotal[] {
  const m = new Map<string, ProductTotal>();
  for (const r of scoped(records, s, f, "product")) {
    const sg = BigInt(r.sign);
    const t = m.get(r.line) ?? {
      productId: r.line,
      orders: 0n,
      qtyQ4: 0n,
      revenueCents: 0n,
      costCents: 0n,
      costMissing: 0n,
    };
    t.orders += sg * BigInt(r.orders);
    t.qtyQ4 += sg * BigInt(r.qty_q4);
    t.revenueCents += sg * BigInt(r.revenue_cents);
    t.costCents += sg * BigInt(r.cost_cents);
    t.costMissing += sg * BigInt(r.cost_missing);
    m.set(r.line, t);
  }
  const out = [...m.values()].filter((t) => t.orders > 0n);
  tooLarge(out.length);
  return out.sort((a, b) =>
    a.revenueCents === b.revenueCents
      ? a.productId.localeCompare(b.productId)
      : a.revenueCents > b.revenueCents
        ? -1
        : 1,
  );
}

/** Compras por cliente e dia (pedidos líquidos > 0), por cliente e dia. */
export function aggregateCustomerDays(
  records: readonly FactRecord[],
  s: AnalyticsScope,
  f: FactFilter,
): CustomerDay[] {
  const m = new Map<string, CustomerDay>();
  for (const r of scoped(records, s, f, "order")) {
    const k = `${r.contact_id}|${r.day}`;
    const sg = BigInt(r.sign);
    const t = m.get(k) ?? { contactId: r.contact_id, day: r.day, orders: 0n, revenueCents: 0n };
    t.orders += sg * BigInt(r.orders);
    t.revenueCents += sg * BigInt(r.revenue_cents);
    m.set(k, t);
  }
  const out = [...m.values()].filter((t) => t.orders > 0n);
  tooLarge(out.length);
  return out.sort((a, b) =>
    a.contactId === b.contactId ? a.day.localeCompare(b.day) : a.contactId.localeCompare(b.contactId),
  );
}

export function customerMonthsOf(days: readonly CustomerDay[]): CustomerMonth[] {
  const m = new Map<string, CustomerMonth>();
  for (const d of days) {
    const month = d.day.slice(0, 7);
    const k = `${d.contactId}|${month}`;
    const t = m.get(k) ?? { contactId: d.contactId, month, orders: 0n, revenueCents: 0n };
    t.orders += d.orders;
    t.revenueCents += d.revenueCents;
    m.set(k, t);
  }
  return [...m.values()].sort((a, b) =>
    a.contactId === b.contactId ? a.month.localeCompare(b.month) : a.contactId.localeCompare(b.contactId),
  );
}

export function customerActivityOf(days: readonly CustomerDay[]): CustomerActivity[] {
  const m = new Map<string, CustomerActivity>();
  for (const d of days) {
    const t = m.get(d.contactId);
    if (!t) {
      m.set(d.contactId, { contactId: d.contactId, firstDay: d.day, lastDay: d.day, ...pick(d) });
      continue;
    }
    if (d.day < t.firstDay) t.firstDay = d.day;
    if (d.day > t.lastDay) t.lastDay = d.day;
    t.orders += d.orders;
    t.revenueCents += d.revenueCents;
  }
  return [...m.values()].sort((a, b) => a.contactId.localeCompare(b.contactId));
}
const pick = (d: CustomerDay) => ({ orders: d.orders, revenueCents: d.revenueCents });

// ---------- percentuais ----------

/** num/den em pct4 (100% = 1.000.000), arredondado meio para cima; den ≤ 0 = 0. */
export function sharePct4(num: bigint, den: bigint): number {
  if (den <= 0n) return 0;
  const n = num * 1_000_000n;
  const q = n / den;
  const r = n % den;
  return Number(2n * (r < 0n ? -r : r) >= den ? (n < 0n ? q - 1n : q + 1n) : q);
}

// ---------- Curva ABC (RF-BI-12) ----------

export type AbcClass = "A" | "B" | "C";
export const ABC_DEFAULT_LIMITS = { aPct4: 800_000, bPct4: 950_000 } as const;
export type AbcRow<T> = T & { cls: AbcClass; sharePct4: number; cumulativePct4: number };
export type AbcSummary = Record<AbcClass, { count: number; value: bigint; sharePct4: number }>;

/**
 * Curva ABC: valores positivos em ordem decrescente (empate pela chave); a classe vem da participação acumulada ANTES
 * do item (< A = A, < B = B, senão C), então o item que cruza o limite fica na classe de cima. Valor ≤ 0 vai para C,
 * no fim e fora do acumulado.
 */
export function abcCurve<T extends { key: string; value: bigint }>(
  items: readonly T[],
  limits: { aPct4: number; bPct4: number } = ABC_DEFAULT_LIMITS,
): { rows: AbcRow<T>[]; total: bigint; summary: AbcSummary } {
  const cmp = (a: T, b: T) => (a.value === b.value ? a.key.localeCompare(b.key) : a.value > b.value ? -1 : 1);
  const pos = items.filter((i) => i.value > 0n).sort(cmp);
  const rest = items.filter((i) => i.value <= 0n).sort(cmp);
  const total = pos.reduce((s, i) => s + i.value, 0n);
  const summary: AbcSummary = {
    A: { count: 0, value: 0n, sharePct4: 0 },
    B: { count: 0, value: 0n, sharePct4: 0 },
    C: { count: 0, value: 0n, sharePct4: 0 },
  };
  let acc = 0n;
  const rows: AbcRow<T>[] = [];
  for (const i of pos) {
    const before = sharePct4(acc, total);
    const cls: AbcClass = before < limits.aPct4 ? "A" : before < limits.bPct4 ? "B" : "C";
    acc += i.value;
    rows.push({ ...i, cls, sharePct4: sharePct4(i.value, total), cumulativePct4: sharePct4(acc, total) });
    summary[cls].count++;
    summary[cls].value += i.value;
  }
  const cum = sharePct4(acc, total);
  for (const i of rest) {
    rows.push({ ...i, cls: "C", sharePct4: 0, cumulativePct4: cum });
    summary.C.count++;
  }
  for (const k of ["A", "B", "C"] as const) summary[k].sharePct4 = sharePct4(summary[k].value, total);
  return { rows, total, summary };
}

// ---------- carteira (RF-BI-11) ----------

export type PortfolioBucket = "active" | "risk" | "inactive";
export const PORTFOLIO_DEFAULTS = { activeDays: 90, riskDays: 180 } as const;

/** Faixa pela recência (dias desde a última compra até `ref`): ≤ ativo, ≤ risco, senão inativo. */
export function portfolioBuckets(
  activity: readonly CustomerActivity[],
  ref: string,
  o: { activeDays: number; riskDays: number } = PORTFOLIO_DEFAULTS,
) {
  const summary: Record<PortfolioBucket, { customers: number; revenueCents: bigint }> = {
    active: { customers: 0, revenueCents: 0n },
    risk: { customers: 0, revenueCents: 0n },
    inactive: { customers: 0, revenueCents: 0n },
  };
  const rows = activity
    .filter((a) => a.lastDay <= ref)
    .map((a) => {
      const recencyDays = daysBetween(a.lastDay, ref);
      const bucket: PortfolioBucket =
        recencyDays <= o.activeDays ? "active" : recencyDays <= o.riskDays ? "risk" : "inactive";
      summary[bucket].customers++;
      summary[bucket].revenueCents += a.revenueCents;
      return { ...a, recencyDays, bucket };
    });
  return { summary, rows };
}

// ---------- cohort e novos × recorrentes (RF-BI-11) ----------

const nextMonth = (m: string) => addDays(`${m}-28`, 7).slice(0, 7);

function firstMonths(months: readonly CustomerMonth[]): Map<string, string> {
  const first = new Map<string, string>();
  for (const r of months) {
    const f = first.get(r.contactId);
    if (f === undefined || r.month < f) first.set(r.contactId, r.month);
  }
  return first;
}

/**
 * Cohort: `n` safras (mês da primeira compra) terminando em `end`; para cada uma, os meses 0.. até `end` (no máximo
 * `n`), com os clientes da safra que compraram no mês e a retenção em pct4 sobre o tamanho da safra.
 */
export function cohortMatrix(months: readonly CustomerMonth[], end: string, n = 12) {
  const cohortMonths = monthsBack(end, n);
  const first = firstMonths(months);
  const active = new Set(months.filter((r) => r.orders > 0n).map((r) => `${r.contactId}|${r.month}`));
  const members = new Map<string, string[]>(cohortMonths.map((m) => [m, []]));
  for (const [c, m] of first) members.get(m)?.push(c);
  const cohorts = cohortMonths.map((month) => {
    const list = members.get(month)!;
    const size = list.length;
    const cells: { offset: number; customers: number; retentionPct4: number }[] = [];
    for (let m = month, offset = 0; m <= end && offset < n; m = nextMonth(m), offset++) {
      const customers = list.filter((c) => active.has(`${c}|${m}`)).length;
      cells.push({ offset, customers, retentionPct4: sharePct4(BigInt(customers), BigInt(size)) });
    }
    return { month, size, cells };
  });
  return { months: cohortMonths, cohorts };
}

/** Por mês: novo = primeira compra no mês; recorrente = já tinha comprado antes. */
export function newVsReturning(months: readonly CustomerMonth[], window: readonly string[]) {
  const first = firstMonths(months);
  const out = new Map(
    window.map((m) => [
      m,
      { month: m, newCustomers: 0, returningCustomers: 0, newRevenueCents: 0n, returningRevenueCents: 0n },
    ]),
  );
  for (const r of months) {
    const row = out.get(r.month);
    if (!row || r.orders <= 0n) continue;
    if (first.get(r.contactId) === r.month) {
      row.newCustomers++;
      row.newRevenueCents += r.revenueCents;
    } else {
      row.returningCustomers++;
      row.returningRevenueCents += r.revenueCents;
    }
  }
  return [...out.values()];
}

// ---------- estoque (RF-BI-10) ----------

export type CoverageBand = "rupture" | "d7" | "d30" | "over" | "no_sales";
export const STOCK_INSIGHT_DEFAULTS = { salesDays: 90, idleDays: 90 } as const;

const roundDiv = (n: bigint, d: bigint) => {
  const q = n / d;
  const r = n % d;
  return 2n * (r < 0n ? -r : r) >= d ? (n < 0n ? q - 1n : q + 1n) : q;
};

/** Cobertura em dias = disponível ÷ média diária (vendido na janela ÷ dias da janela), truncada. */
export function stockCoverage(
  availableQ4: number,
  soldQ4: bigint,
  windowDays: number,
): { avgDailyQ4: number; coverageDays: number | null; band: CoverageBand } {
  if (soldQ4 <= 0n) return { avgDailyQ4: 0, coverageDays: null, band: "no_sales" };
  const avgDailyQ4 = Number(roundDiv(soldQ4, BigInt(windowDays)));
  const avail = BigInt(Math.max(0, availableQ4));
  const coverageDays = Number((avail * BigInt(windowDays)) / soldQ4);
  const band: CoverageBand =
    availableQ4 <= 0 ? "rupture" : coverageDays <= 7 ? "d7" : coverageDays <= 30 ? "d30" : "over";
  return { avgDailyQ4, coverageDays, band };
}

/** Valor do estoque (físico em q4 × custo unitário), arredondado; saldo ≤ 0 = 0. */
export function itemStockValueCents(physicalQ4: number, unitCostCents: number): bigint {
  if (physicalQ4 <= 0 || unitCostCents <= 0) return 0n;
  return roundDiv(BigInt(physicalQ4) * BigInt(unitCostCents), 10_000n);
}

// ---------- SQL do R2 SQL ----------

export type AnalyticsSqlKind = "products" | "customer_months" | "customer_activity";

/**
 * SQL do R2 SQL montado só pelo código: tabela, colunas e formato fixos; os únicos valores são o tenant e a empresa do
 * contexto autenticado (partição, sempre presentes), a faixa de meses/dias, situações, lojas e vendedor — todos
 * conferidos por regex estrita (o que não passa lança; nada é escapado). A tabela derivada com DISTINCT tira o lote
 * reenviado (mesmo export_id). Sem OFFSET (o R2 SQL não tem): LIMIT de ANALYTICS_MAX_ROWS + 1 para detectar o excesso.
 */
export function analyticsSql(
  kind: AnalyticsSqlKind,
  table: string,
  s: AnalyticsScope,
  f: FactFilter,
): string {
  if (!TABLE.test(table)) bad("tabela");
  if (f.statusIds.length === 0) bad("situações");
  const from = safeDate(f.from);
  const to = safeDate(f.to);
  if (from > to) bad("período");
  const q = (v: string) => `'${v}'`;
  const where = [
    `tenant_id = ${q(safeId(s.tenantId, "tenant"))}`,
    `company_id = ${q(safeId(s.companyId, "empresa"))}`,
    `month BETWEEN ${q(from.slice(0, 7))} AND ${q(to.slice(0, 7))}`,
    `day BETWEEN ${q(from)} AND ${q(to)}`,
    `status_id IN (${f.statusIds.map((v) => q(safeId(v, "situação"))).join(", ")})`,
    kind === "products" ? "line <> ''" : "line = ''",
  ];
  if (f.storeKeys) {
    if (f.storeKeys.length === 0) bad("canais");
    where.push(`store_key IN (${f.storeKeys.map((v) => q(safeKey(v, "canal"))).join(", ")})`);
  }
  if (f.sellerKey != null) where.push(`seller_key = ${q(safeKey(f.sellerKey, "vendedor"))}`);
  const base =
    "SELECT DISTINCT export_id, order_id, line, sign, contact_id, month, day, orders, qty_q4, revenue_cents, " +
    `cost_cents, cost_missing FROM ${table} WHERE ${where.join(" AND ")}`;
  const limit = `LIMIT ${ANALYTICS_MAX_ROWS + 1}`;
  if (kind === "products")
    return (
      "SELECT line AS product_id, SUM(sign * orders) AS orders, SUM(sign * qty_q4) AS qty_q4, " +
      "SUM(sign * revenue_cents) AS revenue_cents, SUM(sign * cost_cents) AS cost_cents, " +
      `SUM(sign * cost_missing) AS cost_missing FROM (${base}) f GROUP BY line HAVING SUM(sign * orders) > 0 ` +
      `ORDER BY revenue_cents DESC, product_id ${limit}`
    );
  const days =
    "SELECT contact_id, month, day, SUM(sign * orders) AS orders, SUM(sign * revenue_cents) AS revenue_cents " +
    `FROM (${base}) f GROUP BY contact_id, month, day HAVING SUM(sign * orders) > 0`;
  if (kind === "customer_months")
    return (
      "SELECT contact_id, month, SUM(orders) AS orders, SUM(revenue_cents) AS revenue_cents " +
      `FROM (${days}) d GROUP BY contact_id, month ORDER BY contact_id, month ${limit}`
    );
  return (
    "SELECT contact_id, MIN(day) AS first_day, MAX(day) AS last_day, SUM(orders) AS orders, " +
    `SUM(revenue_cents) AS revenue_cents FROM (${days}) d GROUP BY contact_id ORDER BY contact_id ${limit}`
  );
}

// ---------- encerramento de conta (T3.11): histórico inteiro do tenant ----------

/** Colunas do registro do lago, na ordem do `FactRecord`. */
const FACT_COLUMNS =
  "tenant_id, company_id, month, day, order_id, line, sign, contact_id, status_id, seller_key, store_key, uf, " +
  "orders, qty_q4, revenue_cents, freight_cents, cost_cents, cost_missing, export_id, exported_at";

/** Empresas e meses com lançamentos do tenant no lago (e quantos), para ler o histórico em fatias. */
export function analyticsTenantIndexSql(table: string, tenantId: string): string {
  if (!TABLE.test(table)) bad("tabela");
  return (
    `SELECT company_id, month, COUNT(*) AS n FROM ${table} WHERE tenant_id = '${safeId(tenantId, "tenant")}' ` +
    `GROUP BY company_id, month ORDER BY company_id, month LIMIT ${ANALYTICS_MAX_ROWS + 1}`
  );
}

/** Lançamentos de uma empresa num mês (ou num dia do mês, quando o mês passa do limite de linhas por consulta). */
export function analyticsTenantDumpSql(
  table: string,
  s: { tenantId: string; companyId: string; month: string; day?: string },
): string {
  if (!TABLE.test(table)) bad("tabela");
  const month = safeMonth(s.month);
  const where = [
    `tenant_id = '${safeId(s.tenantId, "tenant")}'`,
    `company_id = '${safeId(s.companyId, "empresa")}'`,
    `month = '${month}'`,
  ];
  if (s.day !== undefined) {
    const day = safeDate(s.day);
    if (day.slice(0, 7) !== month) bad("dia");
    where.push(`day = '${day}'`);
  }
  return (
    `SELECT ${FACT_COLUMNS} FROM ${table} WHERE ${where.join(" AND ")} ` +
    `ORDER BY day, order_id, line, export_id, sign LIMIT ${ANALYTICS_MAX_ROWS + 1}`
  );
}
